import * as React from 'react';

import { ValidationErrorData } from '../../typings/common';
import { ResourceType } from '../common/typings';

export enum CommonOptionName {
    disclaimer = 'disclaimerId',
    disclaimerCountry = 'countryId',
    backgroundColor = 'backgroundColor',
    borderColor = 'borderColor',
    url = 'url',
    fileId = 'fileId',
    color = 'color',
    content = 'content',
    iconColor = 'iconColor',
    value = 'value',
    placeholder = 'placeholder',
    width = 'width',
    height = 'height',
    minHeight = 'minHeight',
    minWidth = 'minWidth'
}

export enum VideoOptionName {
    audio = 'audio_id',
    video = 'video_id',
    packshot = 'packshot_id',
    color = 'color',
    placeholder = 'placeholder',
    borderColor = 'border_color',
    position = 'position',
    customLabel = 'custom_label',
    text = 'text',
    age = 'age',
    textColor = 'text_color',
    backgroundColor = 'background_color'
}

export enum CommonOptionGroupName {
    image = 'image',
    headline = 'headline',
    button = 'button',
    fade = 'fade',
    logo = 'logo',
    description = 'description',
    domain = 'domain',
    ageRestriction = 'ageRestriction',
    disclaimer = 'disclaimer',
    legal = 'legal',
    special = 'special',
    packshot = 'packshot',
    common = 'common'
}

export enum VideoOptionGroupName {
    addition = 'addition',
    body = 'body',
    title = 'title',
    domain = 'domain',
    age = 'age',
    button = 'button',
    legal = 'legal',
    disclaimer = 'disclaimer'
}

export enum OverlayOptionName {
    overlay = 'overlay_id'
}

export type CreativeOptionName = CommonOptionName | VideoOptionName | OverlayOptionName;
export type CreativeOptionGroupName = CommonOptionGroupName | VideoOptionGroupName;
export type CreativeOptionValue = string | null;

export enum CreativeOptionType {
    ColorPicker = 'colorpicker',
    Text = 'text',
    Age = 'age',
    File = 'file',
    Select = 'select',
    Position = 'position',
    Disclaimer = 'disclaimer'
}

export type CreativeOptionsConfig = Record<string, CreativeOptionGroupConfig>;

export type CreativeOptionsGroupErrors = Partial<Record<CreativeOptionName, ValidationErrorData[]>>;

export type CreativeOptionsErrors = Partial<Record<CreativeOptionGroupName, CreativeOptionsGroupErrors>>;

interface CreativeImageGroupApiValue {
    type: CommonOptionGroupName.image | CommonOptionGroupName.logo;
    available: boolean;
    options: {
        width: number;
        height: number;
    };
    // в коммон креативах в файловых параметрах здесь указывается id медиасета,
    // из которого потом можно вытащить урл файла
    mediaSet?: string;
}

interface CreativeOptionGroupBaseApiValue {
    type: Exclude<CreativeOptionGroupName, CommonOptionGroupName.image | CommonOptionGroupName.logo>;
    // чекнул пользователь опцию или нет
    available: boolean;
    // среди этих опций может быть еще placeholder
    options: Record<string, CreativeOptionValue>;
    // в коммон креативах сюда приходит limit для текстовых параметров
    maxLength?: number;
    mediaSet?: string;
}

export type CreativeOptionGroupApiValue = CreativeImageGroupApiValue | CreativeOptionGroupBaseApiValue;

export interface CreativeImageGroupValue {
    type: CommonOptionGroupName.image | CommonOptionGroupName.logo;
    options: {
        width: number;
        height: number;
        [CommonOptionName.fileId]: string | null;
    };
    visible?: boolean;
    // в коммон креативах в файловых параметрах здесь указывается id медиасета,
    // из которого потом можно вытащить урл файла
    mediaSet?: string;
}
export interface CreativeOptionsBaseGroupValue {
    type: Exclude<CreativeOptionGroupName, CommonOptionGroupName.image | CommonOptionGroupName.logo>;
    options: Record<string, CreativeOptionValue>;
    visible?: boolean;
    optionsFromPreviewData?: string[];
    externalHidden?: boolean;
    // в коммон креативах сюда приходит limit для текстовых параметров
    maxLength?: number;
    mediaSet?: string;
}

export type CreativeGroupOptions =
    | {
          width: number;
          height: number;
          [CommonOptionName.fileId]: string | null;
      }
    | Record<string, CreativeOptionValue>;
export type CreativeOptionsGroupValue = CreativeOptionsBaseGroupValue | CreativeImageGroupValue;
export type CreativeOptionsValue = CreativeOptionsGroupValue[];

export interface CreativeOptionGroupConfig {
    title: string;
    tooltip?: string | React.ReactNode;
    canBeHidden?: boolean;
    options: Array<CreativeOptionData>;

    // Параметр объявляется в конфиге и нигде не используется
    isPrivate?: boolean;
}

export interface CreativeOptionBaseData {
    type: CreativeOptionType;
    editable?: boolean;
    visible?: boolean;
    defaultValue?: string | null;
    name: CreativeOptionName;
    title: string;
    tooltip?: string | React.ReactNode;
}

export type CreativeOptionColorPickerData = CreativeOptionBaseData & {
    type: CreativeOptionType.ColorPicker;
    allowedValues?: string[];
    dependsOnVideoColor?: boolean;
};

export type CreativeOptionTextData = CreativeOptionBaseData & {
    type: CreativeOptionType.Text;
    limit?: number;
};

export type CreativeOptionAgeData = CreativeOptionBaseData & {
    type: CreativeOptionType.Age;
    allowedValues: string[];
};

export type CreativeOptionSelectData = CreativeOptionBaseData & {
    type: CreativeOptionType.Select;
    allowedValues: string[];
};

export type CreativeOptionPositionData = CreativeOptionBaseData & {
    type: CreativeOptionType.Position;
    allowedValues: string[];
};

export interface DisclaimerBaseItem {
    id: number;
    name: string;
}

export interface Disclaimer extends DisclaimerBaseItem {
    groupId: number;
}

export interface DisclaimerCountry {
    id: number;
    name: string;
    items: Disclaimer[];
}

export interface DisclaimerGroup extends DisclaimerBaseItem {
    alias: string;
}

export type CreativeOptionDisclaimerData = CreativeOptionBaseData & {
    type: CreativeOptionType.Disclaimer;

    items: DisclaimerCountry[];
    groups: DisclaimerGroup[];
};

export enum CreativeOptionSourceType {
    upload = 'upload',
    link = 'link',
    recent = 'recent',
    stock = 'stock',
    reset = 'reset',
    idea = 'idea',
    crop = 'crop',
    smartCenter = 'smart-center'
}

export type CreativeOptionGallerySourceType =
    | CreativeOptionSourceType.recent
    | CreativeOptionSourceType.stock
    | CreativeOptionSourceType.idea;

export enum CreativePositionOptionValue {
    LeftTop = 'left-top',
    CenterTop = 'center-top',
    RightTop = 'right-top',
    LeftCenter = 'left-center',
    CenterCenter = 'center-center',
    RightCenter = 'right-center',
    LeftBottom = 'left-bottom',
    CenterBottom = 'center-bottom',
    RightBottom = 'right-bottom'
}

export const CREATIVE_POSITION_OPTION_VALUES: CreativePositionOptionValue[] = [
    CreativePositionOptionValue.LeftTop,
    CreativePositionOptionValue.CenterTop,
    CreativePositionOptionValue.RightTop,
    CreativePositionOptionValue.LeftCenter,
    CreativePositionOptionValue.CenterCenter,
    CreativePositionOptionValue.RightCenter,
    CreativePositionOptionValue.LeftBottom,
    CreativePositionOptionValue.CenterBottom,
    CreativePositionOptionValue.RightBottom
];

// Возможно, описание типа неполное и/или неточное.
export type CreativeOptionBaseFileData = CreativeOptionBaseData & {
    type: CreativeOptionType.File;
    resourceType: ResourceType;
    sources: CreativeOptionSourceType[];

    editorId: string;

    // Параметр уже не используется
    // отвечает за вид кнопки - селект или просто кнопка
    isMultisource?: boolean;

    // Параметры нигде не используются, однако приходят с бекенда
    cpmAudio?: boolean;
    packshot?: boolean;
    isPackshot?: boolean;
    isCpmAudio?: boolean;
};

export type CreativeOptionImageFileData = CreativeOptionBaseFileData & {
    resourceType: ResourceType.image;

    isCroppable: boolean;
    isSmartCenterable?: boolean;
};

export type CreativeOptionAudioFileData = CreativeOptionBaseFileData & {
    resourceType: ResourceType.audio;
};

export type CreativeOptionVideoFileData = CreativeOptionBaseFileData & {
    resourceType: ResourceType.video;
};

export type CreativeOptionFileData =
    | CreativeOptionVideoFileData
    | CreativeOptionAudioFileData
    | CreativeOptionImageFileData;

export type CreativeOptionData =
    | CreativeOptionColorPickerData
    | CreativeOptionTextData
    | CreativeOptionAgeData
    | CreativeOptionFileData
    | CreativeOptionSelectData
    | CreativeOptionPositionData
    | CreativeOptionDisclaimerData;

export interface CreativeOptionsData {
    name: string;
    optionsConfig: CreativeOptionsConfig;
    optionsValue: CreativeOptionsValue;
    errors?: CreativeOptionsErrors;
}
